"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.GlassScrollLayout = GlassScrollLayout;
var jsx_runtime_1 = require("react/jsx-runtime");
// ================================================================
// v2/components/GlassScrollLayout/index.tsx
// 固定ぼかし背景 (Amazon Music 風) を持つスクロール画面レイアウト。
// 背景ヒーロー画像を画面に貼り付けて固定し、その上をコンテンツが流れる。
// EventDetail v2 を起点に、後続 LotteryDetail / ArtistTop でも横断利用する。
//
// レイヤー構造 (奥→手前):
//   奥 (z:0 / sticky)  — ヒーロー画像を filter:blur で全面ぼかし + tint スクリム。
//                        スクロールしても動かず画面に固定される。
//   中 (z:0 / absolute)— topScrim: ページ上端の白 (dark は黒) グラデーション。
//                        ページ側に固定されるのでスクロールで流れていく。
//   手前 (z:1)         — children (AppHeader / コンテンツ)。この中の GlassCard 等の
//                        backdrop-filter は、背後の固定ぼかし背景を透かしてさらに
//                        ガラス化する (= すりガラスの重なり)。
//
// スクロール検知: 先頭に 1px センチネルを置き、IntersectionObserver で上端から
//   外れたら scrolled=true を render-prop で children に渡す (AppHeader のガラス切替用)。
//   BaseLayout 側の overflow により window では sticky が効かないため、この
//   レイアウト自身が height:100dvh のスクロールコンテナを持つ。
//
// 色・ぼかしは theme.palette.v2 (useV2Palette) とトークンから取得する (ハードコード禁止)。
// ================================================================
var material_1 = require("@mui/material");
var react_1 = require("react");
var theme_1 = require("../../theme");
var tokens_1 = require("../../tokens");
/**
 * 画像 URL を CSS の url() に安全に埋め込む。
 *
 * 引用符無しの url() トークンには `)` / 引用符 / 空白 / 制御文字を含められず、
 * サムネイル URL にそれらが混ざると宣言が壊れる (背景が消える / 後続の CSS を
 * 差し込めてしまう)。引用符で囲み、囲みを破れる文字だけをパーセントエンコード
 * する (`%` を素通しするため、既にエンコード済みの署名付き URL を二重に
 * エンコードしない)。
 */
var cssUrl = function (src) {
    return "url(\"".concat(src.replace(/["\\]/g, encodeURIComponent).replace(/[\n\r\f]/g, ''), "\")");
};
function GlassScrollLayout(_a) {
    var backgroundSrc = _a.backgroundSrc, _b = _a.backgroundBlur, backgroundBlur = _b === void 0 ? tokens_1.blur.backdrop : _b, overlayColor = _a.overlayColor, _c = _a.contentMaxWidth, contentMaxWidth = _c === void 0 ? 600 : _c, _d = _a.topScrim, topScrim = _d === void 0 ? false : _d, children = _a.children;
    var p = (0, theme_1.useV2Palette)();
    var scrollRef = (0, react_1.useRef)(null);
    var sentinelRef = (0, react_1.useRef)(null);
    var _e = (0, react_1.useState)(false), scrolled = _e[0], setScrolled = _e[1];
    // 先頭センチネルが上端から外れたら scrolled=true。root はこのスクロールコンテナ。
    (0, react_1.useEffect)(function () {
        var _a;
        var el = sentinelRef.current;
        if (!el || typeof IntersectionObserver === 'undefined')
            return;
        var io = new IntersectionObserver(function (_a) {
            var entry = _a[0];
            return setScrolled(!entry.isIntersecting);
        }, { root: (_a = scrollRef.current) !== null && _a !== void 0 ? _a : null, threshold: 0 });
        io.observe(el);
        return function () { return io.disconnect(); };
    }, []);
    return ((0, jsx_runtime_1.jsxs)(material_1.Box, { ref: scrollRef, sx: {
            position: 'relative',
            height: '100dvh',
            overflowY: 'auto',
            overflowX: 'hidden',
            overscrollBehaviorY: 'none',
            backgroundColor: p.background.base,
            // スクロールバー非表示 (utoniq-ui 内で自己完結)
            '&::-webkit-scrollbar': { display: 'none' },
            scrollbarWidth: 'none',
            msOverflowStyle: 'none',
        }, children: [backgroundSrc && ((0, jsx_runtime_1.jsxs)(material_1.Box, { "aria-hidden": true, sx: {
                    position: 'sticky',
                    top: 0,
                    height: '100dvh',
                    marginBottom: '-100dvh',
                    zIndex: 0,
                    overflow: 'hidden',
                    pointerEvents: 'none',
                }, children: [(0, jsx_runtime_1.jsx)(material_1.Box, { sx: {
                            position: 'absolute',
                            inset: "-".concat(backgroundBlur, "px"),
                            backgroundImage: cssUrl(backgroundSrc),
                            backgroundSize: 'cover',
                            backgroundPosition: 'center',
                            filter: "blur(".concat(backgroundBlur, "px)"),
                        } }), (0, jsx_runtime_1.jsx)(material_1.Box, { sx: {
                            position: 'absolute',
                            inset: 0,
                            backgroundColor: overlayColor !== null && overlayColor !== void 0 ? overlayColor : p.background.surface,
                        } })] })), topScrim && ((0, jsx_runtime_1.jsx)(material_1.Box, { "aria-hidden": true, sx: {
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    height: '100vh',
                    '@supports (height: 100svh)': { height: '100svh' },
                    zIndex: 0,
                    pointerEvents: 'none',
                    backgroundImage: "linear-gradient(".concat(p.background.topScrimFrom, ", ").concat(p.background.topScrimTo, ")"),
                } })), (0, jsx_runtime_1.jsxs)(material_1.Box, { sx: {
                    position: 'relative',
                    zIndex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    minHeight: '100%',
                    width: '100%',
                    maxWidth: contentMaxWidth,
                    marginX: 'auto',
                }, children: [(0, jsx_runtime_1.jsx)(material_1.Box, { ref: sentinelRef, sx: { height: '1px', flexShrink: 0 } }), typeof children === 'function' ? children({ scrolled: scrolled }) : children] })] }));
}
